import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { CheckCircle2, Plus, Trash2, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Button, Input, Skeleton, Textarea } from '@/components/ui';
import { ApiError, request } from '@/lib/api';
import { setLocale } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { PublicShell } from './public-shell';

/* ---------------------------------------------------------------------------
   קביעת טיפול מקישור תזכורת.

   הלקוח לא בוחר שעה מדויקת — הוא לא יודע מה הלו"ז של הטכנאי, ואנחנו לא
   רוצים להבטיח משבצת שאולי תפוסה. הוא נותן עד שלושה חלונות נוחים, ובעל
   העסק קובע מתוכם ושולח קישור סטטוס עם המועד הסופי.
   --------------------------------------------------------------------------- */

const viewSchema = z.object({
  businessName: z.string(),
  equipment: z.object({ kind: z.string(), location: z.string().nullable() }),
  alreadyBooked: z.boolean(),
  minDate: z.string(),
  maxDate: z.string(),
  locale: z.enum(['he', 'en']).catch('he').optional(),
});

type Part = 'morning' | 'noon' | 'evening';
const PARTS = [
  { value: 'morning', labelKey: 'booking.parts.morning', hint: '8–12' },
  { value: 'noon', labelKey: 'booking.parts.noon', hint: '12–16' },
  { value: 'evening', labelKey: 'booking.parts.evening', hint: '16–19' },
] as const satisfies ReadonlyArray<{ value: Part; labelKey: string; hint: string }>;

export function BookingPage() {
  const { t } = useTranslation('public');
  const { token } = useParams({ from: '/c/b/$token' });
  const view = useQuery({
    queryKey: ['public-booking', token],
    queryFn: ({ signal }) => request(`/public/booking/${token}`, { schema: viewSchema, signal }),
    retry: false,
  });

  // הדף מוצג בשפת העסק, לא של הדפדפן.
  const locale = view.data?.locale;
  useEffect(() => {
    if (locale) setLocale(locale, { reload: false });
  }, [locale]);

  const [windows, setWindows] = useState<Array<{ date: string; part: Part }>>([{ date: '', part: 'morning' }]);
  const [note, setNote] = useState('');

  const book = useMutation({
    mutationFn: () =>
      request(`/public/booking/${token}`, {
        method: 'POST',
        body: { windows: windows.filter((w) => w.date), ...(note.trim() && { note: note.trim() }) },
      }),
  });

  if (view.isLoading) {
    return (
      <PublicShell>
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-6 h-40 w-full" />
      </PublicShell>
    );
  }

  if (view.isError || !view.data) {
    return (
      <PublicShell>
        <Message icon={XCircle} title={t('booking.invalidTitle')} body={t('booking.invalidBody')} />
      </PublicShell>
    );
  }

  const v = view.data;
  const conflict = book.error instanceof ApiError && book.error.status === 409;

  if (book.isSuccess || v.alreadyBooked || conflict) {
    return (
      <PublicShell business={v.businessName}>
        <Message
          icon={CheckCircle2}
          tone="success"
          title={t('booking.sentTitle')}
          body={t('booking.sentBody', { business: v.businessName })}
        />
      </PublicShell>
    );
  }

  const valid = windows.some((w) => w.date);
  const what = v.equipment.location ? `${v.equipment.kind} (${v.equipment.location})` : v.equipment.kind;

  return (
    <PublicShell business={v.businessName}>
      <h1 className="text-xl font-semibold text-fg">{t('booking.title')}</h1>
      <p className="mt-1 text-sm text-fg-muted">{what}</p>

      <form
        className="mt-6 space-y-5"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) book.mutate();
        }}
      >
        <fieldset className="space-y-4">
          <legend className="text-sm font-medium text-fg">{t('booking.when')}</legend>
          {windows.map((w, i) => (
            <div key={i} className="rounded-2xl border border-border bg-surface p-4">
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  aria-label={t('booking.dateAria', { n: i + 1 })}
                  min={v.minDate}
                  max={v.maxDate}
                  value={w.date}
                  onChange={(e) => setWindows(windows.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))}
                  className="flex-1"
                />
                {windows.length > 1 ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={t('booking.removeAria', { n: i + 1 })}
                    onClick={() => setWindows(windows.filter((_, j) => j !== i))}
                  >
                    <Trash2 aria-hidden />
                  </Button>
                ) : null}
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2" role="radiogroup" aria-label={t('booking.partAria', { n: i + 1 })}>
                {PARTS.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    role="radio"
                    aria-checked={w.part === p.value}
                    onClick={() => setWindows(windows.map((x, j) => (j === i ? { ...x, part: p.value } : x)))}
                    className={cn(
                      'min-h-12 rounded-xl border px-2 py-2 text-sm transition-colors',
                      w.part === p.value
                        ? 'border-accent bg-accent-subtle font-medium text-accent'
                        : 'border-border text-fg-muted hover:border-border-strong',
                    )}
                  >
                    {t(p.labelKey)}
                    <span className="block text-2xs opacity-80" dir="ltr">
                      {p.hint}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          ))}
          {windows.length < 3 ? (
            <Button type="button" variant="ghost" onClick={() => setWindows([...windows, { date: '', part: 'morning' }])}>
              <Plus aria-hidden />
              {t('booking.addOption')}
            </Button>
          ) : null}
        </fieldset>

        <div className="space-y-2">
          <label htmlFor="booking-note" className="text-sm font-medium text-fg">
            {t('booking.noteLabel')} <span className="font-normal text-fg-subtle">{t('booking.optional')}</span>
          </label>
          <Textarea
            id="booking-note"
            rows={2}
            maxLength={500}
            value={note}
            placeholder={t('booking.notePlaceholder')}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        {book.isError && !conflict ? (
          <p role="alert" className="text-sm text-danger">
            {book.error instanceof ApiError && book.error.status === 400
              ? t('booking.outOfRange')
              : t('booking.sendFailed')}
          </p>
        ) : null}

        <Button type="submit" size="lg" className="h-14 w-full text-base" disabled={!valid} loading={book.isPending}>
          {t('booking.submit')}
        </Button>
      </form>
    </PublicShell>
  );
}

function Message({
  icon: Icon,
  title,
  body,
  tone,
}: {
  icon: typeof XCircle;
  title: string;
  body: string;
  tone?: 'success';
}) {
  return (
    <div className="py-10 text-center">
      <Icon className={cn('mx-auto size-12', tone === 'success' ? 'text-success' : 'text-fg-subtle')} aria-hidden />
      <h1 className="mt-3 text-lg font-semibold text-fg">{title}</h1>
      <p className="mt-1 text-sm text-fg-muted">{body}</p>
    </div>
  );
}
